import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchCompany,
  fetchPreferences,
  fetchCurrencies,
  fetchExchangeRates,
} from "../api/settingsService";
import { STALE } from "@/lib/query-constants";
import { resolveFrameworkFromIds, useFrameworkStore } from "@/stores/framework";
import { useAuthStore } from "@/stores/auth";
import { decodeJwt } from "@/utils/token";
import { usePermissionsStore } from "@/stores/permissions";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";
import { isAiEligiblePlan } from "@/lib/aiAccess";
import { isSupplierRole } from "@/lib/workspaceAccess";

export function useCompany() {
  return useQuery({
    queryKey: ["company"],
    queryFn: () => fetchCompany(),
  });
}

export function usePreferences(fetchReports = false) {
  const company = useAuthStore((state) => state.user?.companyId);
  const query = useQuery({
    queryKey: ["preferences", fetchReports, company],
    queryFn: () => fetchPreferences(fetchReports),
  });

  // Hydrating here rather than in each consumer means every `getProtocol()`
  // caller sees the server's framework as soon as preferences resolve. An id we
  // do not recognise is recorded instead of being coerced to ISO (plan 6.4).
  useEffect(() => {
    const frameworkIds = query.data?.companyPreference?.reports?.framework;
    const resolved = resolveFrameworkFromIds(frameworkIds);
    if (resolved) {
      useFrameworkStore
        .getState()
        .hydrate(resolved.framework, resolved.reportTypeId);
      return;
    }
    if (frameworkIds && frameworkIds.length > 0) {
      useFrameworkStore.getState().markUnresolved(frameworkIds[0]);
    }
  }, [query.data]);

  return query;
}

/** AI requires a verified active subscription outside the Basic/Lite tiers. */
export function useAiPlanEligible(): boolean {
  const planCode = usePermissionsStore((state) => state.planCode);
  const { active } = useSubscriptionAccess();
  return active && isAiEligiblePlan(planCode);
}

/**
 * AI controls require both subscription eligibility and this company's saved
 * preference. Unknown preferences and non-company principals fail closed.
 */
export function useAiAssistantEnabled(): boolean {
  const token = useAuthStore((state) => state.token);
  const role = useAuthStore((state) => state.user?.role);
  const isCompanyUser =
    token != null && decodeJwt(token)?.issuer?.type === "B2B";
  const planEligible = useAiPlanEligible();
  const { data } = usePreferences();
  const enabled = data?.companyPreference?.aiAssistantEnabled;
  return (
    !isSupplierRole(role) && isCompanyUser && planEligible && enabled === true
  );
}

export function useCurrencies() {
  return useQuery({
    queryKey: ["currencies"],
    queryFn: () => fetchCurrencies(),
    staleTime: STALE.TEN_MIN,
  });
}

export function useExchangeRates() {
  return useQuery({
    queryKey: ["exchange-rates"],
    queryFn: () => fetchExchangeRates(),
  });
}
